import { OrderValidationFormat } from "@lib/validators/regex/orderNumber";
import { ExtraPriceType, SizeLocales } from "../../lib/constants/filtersConstants";
import type { SiteConfigEntry } from "./types";
import { Market } from "./types";

const loyaltyEnabledForEnv = process.env.NEXT_PUBLIC_LOYALTY_ENABLED === "true";
const isKlarnaSiteMessagingEnabled = process.env.NEXT_PUBLIC_KLARNA_WIDGETS_ENABLED === "true";
const notifySizeAvailabilityEnabled =
    process.env.NEXT_PUBLIC_NOTIFY_SIZE_AVAILABILITY_ENABLED === "true";
const isLocalizedCategoryFacet = process.env.NEXT_PUBLIC_LOCALIZED_CATEGORY_FACETS === "true";
const isLiveChatEnabled = process.env.NEXT_PUBLIC_LIVE_CHAT_ENABLED === "true";
const isRaffleEnabled = process.env.NEXT_PUBLIC_RAFFLE_ENABLED === "true";

export const siteConfig: Record<Market, SiteConfigEntry> = {
    [Market.MY]: {
        country: "MY",
        countryName: "Malaysia",
        localizedCountryName: "Malaysia",
        language: ["en"],
        cmsLocaleTagId: "localeEnMy",
        currency: "MYR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [
            SizeLocales.US,
            SizeLocales.CA,
            SizeLocales.EU,
            SizeLocales.UK,
            SizeLocales.CM,
        ],
        checkout: {
            countryCode: "+60",
        },
        geo: {
            centerLat: 4.210484,
            centerLng: 101.975766,
            swLat: 0.853821,
            swLng: 99.640573,
            neLat: 7.363468,
            neLng: 119.269362,
            // Initial Zoom Level defined by UX team
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.MY],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasGrabPayWidget: true,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        merchantReturnDays: 14,
        isOrderHistoryRoutesAllowed: true,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
    },
    [Market.SG]: {
        country: "SG",
        countryName: "Singapore",
        localizedCountryName: "Singapore",
        language: ["en"],
        cmsLocaleTagId: "localeEnSg",
        currency: "SGD",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+65",
        },
        geo: {
            centerLat: 1.352083,
            centerLng: 103.819836,
            swLat: 1.166398,
            swLng: 103.605575,
            neLat: 1.470772,
            neLng: 104.085557,
            initZoomLevel: 11,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.SG],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        hasGrabPayWidget: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        merchantReturnDays: 14,
        isOrderHistoryRoutesAllowed: true,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
    },
    [Market.ES]: {
        country: "ES",
        countryName: "Spain",
        localizedCountryName: "España",
        language: ["es"],
        cmsLocaleTagId: "localeEsEs",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+34",
        },
        geo: {
            centerLat: 40.463667,
            centerLng: -3.74922,
            swLat: 27.637789,
            swLng: -18.160788,
            neLat: 43.79238,
            neLng: 4.327784,
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.ES],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS, ExtraPriceType.RRP],
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOrderHistoryRoutesAllowed: true,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.EE]: {
        country: "EE",
        countryName: "Estonia",
        localizedCountryName: "Eesti",
        language: ["et"],
        cmsLocaleTagId: "localeEtEe",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+372",
        },
        geo: {
            centerLat: 58.595272,
            centerLng: 25.013607,
            swLat: 57.509316,
            swLng: 21.764313,
            neLat: 59.685685,
            neLng: 28.210139,
            initZoomLevel: 8,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.EE],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOrderHistoryRoutesAllowed: true,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.IN]: {
        country: "IN",
        countryName: "India",
        localizedCountryName: "India",
        language: ["en"],
        cmsLocaleTagId: "localeEnIn",
        currency: "INR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+91",
        },
        geo: {
            centerLat: 20.593684,
            centerLng: 78.96288,
            swLat: 6.753516,
            swLng: 68.162386,
            neLat: 35.504475,
            neLng: 97.395555,
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.IN],
        isProductInfoRequired: true,
        includesAllTaxes: true,
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        merchantReturnDays: 15,
        isOrderHistoryRoutesAllowed: true,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
    },
    [Market.KR]: {
        country: "KR",
        countryName: "South Korea",
        localizedCountryName: "대한민국",
        language: ["ko"],
        cmsLocaleTagId: "localeKoKr",
        currency: "KRW",
        shoeSizingConventions: [SizeLocales.KR, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.KR, SizeLocales.EU, SizeLocales.US, SizeLocales.CM],
        fractionDigits: 0,
        checkout: {
            countryCode: "+82",
        },
        geo: {
            centerLat: 35.907757,
            centerLng: 127.766922,
            swLat: 33.10611,
            swLng: 124.608139,
            neLat: 38.616931,
            neLng: 130.923218,
            initZoomLevel: 8,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.KR],
        isProductInfoRequired: true,
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        merchantReturnDays: 7,
        isOrderHistoryRoutesAllowed: true,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
    },
    [Market.CA]: {
        country: "CA",
        countryName: "Canada",
        localizedCountryName: "Canada",
        language: ["en", "fr"],
        cmsLocaleTagId: "localeEnCa",
        currency: "CAD",
        shoeSizingConventions: [SizeLocales.CA, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.CA, SizeLocales.US, SizeLocales.EU, SizeLocales.CM],
        fractionDigits: 2,
        checkout: {
            countryCode: "+1",
            requiresSalesTaxEstimation: true,
            hasShippingFees: true,
            hasFooter: true,
        },
        geo: {
            centerLat: 56.130366,
            centerLng: -106.346771,
            swLat: 41.676556,
            swLng: -141.00187,
            neLat: 83.115061,
            neLng: -52.619409,
            initZoomLevel: 4,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.CA],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        liveChat: isLiveChatEnabled,
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP, ExtraPriceType.ADJUSTED],
        isOrderHistoryRoutesAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasAccessibilityStatementLink: true,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        hasRaffle: isRaffleEnabled,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        eccoClubConfig: {
            hasPointsAndTiers: false,
        },
        showShippingNetCost: true,
    },
    [Market.US]: {
        country: "US",
        countryName: "United States",
        localizedCountryName: "United States",
        language: ["en"],
        cmsLocaleTagId: "localeEnUs",
        currency: "USD",
        shoeSizingConventions: [SizeLocales.US, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.CA, SizeLocales.EU, SizeLocales.CM],
        fractionDigits: 2,
        checkout: {
            countryCode: "+1",
            requiresSalesTaxEstimation: true,
            hasShippingFees: true,
            hasShippingDetails: true,
            hasFooter: true,
        },
        geo: {
            centerLat: 39.50024,
            centerLng: -98.350891,
            swLat: 25.820898,
            swLng: -124.712891,
            neLat: 49.38024,
            neLng: -66.940662,
            initZoomLevel: 5,
            imperialMetricSystem: true,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.US],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        liveChat: isLiveChatEnabled,
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP, ExtraPriceType.ADJUSTED],
        isOrderHistoryRoutesAllowed: true,
        supportsGlobalPrivacyControl: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasAccessibilityStatementLink: true,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        hasRaffle: isRaffleEnabled,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        eccoClubConfig: {
            hasPointsAndTiers: false,
        },
        showShippingNetCost: true,
    },
    [Market.AU]: {
        country: "AU",
        countryName: "Australia",
        localizedCountryName: "Australia",
        language: ["en"],
        cmsLocaleTagId: "localeEnAu",
        currency: "AUD",
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [SizeLocales.AU, SizeLocales.EU, SizeLocales.CN, SizeLocales.CM],
        fractionDigits: 0,
        checkout: {
            countryCode: "+61",
            requiresSalesTaxEstimation: true,
        },
        geo: {
            centerLat: -25.274398,
            centerLng: 133.775136,
            swLat: -54.777219,
            swLng: 112.921454,
            neLat: -9.221084,
            neLng: 159.255528,
            initZoomLevel: 5,
            imperialMetricSystem: true,
        },
        liveChat: isLiveChatEnabled,
        orderNumberFormatRegex: OrderValidationFormat[Market.AU],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        isOrderHistoryRoutesAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        hasRaffle: isRaffleEnabled,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        eccoClubConfig: {
            hasPointsAndTiers: false,
        },
    },
    [Market.NZ]: {
        country: "NZ",
        countryName: "New Zealand",
        localizedCountryName: "New Zealand",
        language: ["en"],
        cmsLocaleTagId: "localeEnNz",
        currency: "NZD",
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [SizeLocales.AU, SizeLocales.EU, SizeLocales.CN, SizeLocales.CM],
        fractionDigits: 0,
        checkout: {
            countryCode: "+64",
            requiresSalesTaxEstimation: true,
        },
        geo: {
            centerLat: -41.2865,
            centerLng: 174.7762,
            swLat: -47.2896,
            swLng: 166.4258,
            neLat: -34.3893,
            neLng: 178.566,
            initZoomLevel: 5,
            imperialMetricSystem: true,
        },
        liveChat: isLiveChatEnabled,
        orderNumberFormatRegex: OrderValidationFormat[Market.NZ],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        isOrderHistoryRoutesAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        hasRaffle: isRaffleEnabled,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        eccoClubConfig: {
            hasPointsAndTiers: false,
        },
    },
    [Market.JP]: {
        country: "JP",
        countryName: "Japan",
        localizedCountryName: "日本",
        language: ["ja"],
        cmsLocaleTagId: "localeJaJp",
        currency: "JPY",
        shoeSizingConventions: [SizeLocales.JP, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.JP, SizeLocales.EU, SizeLocales.US, SizeLocales.CM],
        fractionDigits: 3,
        checkout: {
            countryCode: "+81",
            requiresSalesTaxEstimation: true,
            hasTaxInclusivePricing: true,
            minAddressLookupLength: 7,
        },
        geo: {
            centerLat: 36.204824,
            centerLng: 138.252924,
            swLat: 24.046045,
            swLng: 122.93383,
            neLat: 45.522772,
            neLng: 153.98743,
            initZoomLevel: 5,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.JP],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.RRP],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: true,
        amazonPay: {
            enabled: process.env.NEXT_PUBLIC_AMAZON_PAY_ENABLED === "true",
            bannerEnabled: true,
            checkoutJsUrl: "https://static-fe.payments-amazon.com/checkout.js",
            merchantId: "A2YSIM42GJ1TXQ",
            region: "jp",
        },
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        merchantReturnDays: 14,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
    },
    [Market.GB]: {
        country: "GB",
        countryName: "United Kingdom",
        localizedCountryName: "United Kingdom",
        language: ["en"],
        cmsLocaleTagId: "localeEnGb",
        currency: "GBP",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.UK, SizeLocales.EU, SizeLocales.CM],
        checkout: {
            countryCode: "+44",
        },
        geo: {
            centerLat: 55.378051,
            centerLng: -3.435973,
            swLat: 49.562515,
            swLng: -12.649357,
            neLat: 59.86067,
            neLng: 3.976555,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasRaffle: isRaffleEnabled,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: true,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.PL]: {
        country: "PL",
        countryName: "Poland",
        localizedCountryName: "Polska",
        language: ["pl"],
        cmsLocaleTagId: "localePlPl",
        currency: "PLN",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+48",
        },
        geo: {
            centerLat: 51.919438,
            centerLng: 19.145136,
            swLat: 49.002025,
            swLng: 14.122864,
            neLat: 54.835812,
            neLng: 24.145893,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS, ExtraPriceType.RRP],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        showParcelLockerId: true,
        hideParcelLockerWorkingHours: true,
        hideAvailabilityInStore: true,
        hasRaffle: isRaffleEnabled,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.PT]: {
        country: "PT",
        countryName: "Portugal",
        localizedCountryName: "Portugal",
        language: ["pt"],
        cmsLocaleTagId: "localePtPt",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+351",
        },
        geo: {
            centerLat: 39.399872,
            centerLng: -8.224454,
            swLat: 32.40374,
            swLng: -31.27533,
            neLat: 42.154205,
            neLng: -6.190209,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.HU]: {
        country: "HU",
        countryName: "Hungary",
        localizedCountryName: "Magyarország",
        language: ["hu"],
        cmsLocaleTagId: "localeHuHu",
        currency: "HUF",
        fractionDigits: 0,
        roundingUnit: 5,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+36",
        },
        geo: {
            centerLat: 47.162494,
            centerLng: 19.503304,
            swLat: 45.737089,
            swLng: 16.113387,
            neLat: 48.585234,
            neLng: 22.898122,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.CZ]: {
        country: "CZ",
        countryName: "Czechia",
        localizedCountryName: "Česko",
        language: ["cs"],
        cmsLocaleTagId: "localeCsCz",
        currency: "CZK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+420",
        },
        geo: {
            centerLat: 49.817492,
            centerLng: 15.472962,
            swLat: 48.551808,
            swLng: 12.090589,
            neLat: 51.055719,
            neLng: 18.859236,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.HR]: {
        country: "HR",
        countryName: "Croatia",
        localizedCountryName: "Hrvatska",
        language: ["hr"],
        cmsLocaleTagId: "localeHrHr",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+385",
        },
        geo: {
            centerLat: 45.1,
            centerLng: 15.2,
            swLat: 42.392265,
            swLng: 13.489691,
            neLat: 46.555223,
            neLng: 19.448052,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.BE]: {
        country: "BE",
        countryName: "Belgium",
        localizedCountryName: "België",
        language: ["nl", "fr"],
        cmsLocaleTagId: "localeNlBe",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+32",
        },
        geo: {
            centerLat: 50.503887,
            centerLng: 4.469936,
            swLat: 49.497013,
            swLng: 2.544795,
            neLat: 51.505145,
            neLng: 6.408124,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.SI]: {
        country: "SI",
        countryName: "Slovenia",
        localizedCountryName: "Slovenija",
        language: ["sl"],
        cmsLocaleTagId: "localeSlSi",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+386",
        },
        geo: {
            centerLat: 46.151241,
            centerLng: 14.995463,
            swLat: 45.421674,
            swLng: 13.375336,
            neLat: 46.876659,
            neLng: 16.596686,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.SK]: {
        country: "SK",
        countryName: "Slovakia",
        localizedCountryName: "Slovensko",
        language: ["sk"],
        cmsLocaleTagId: "localeSkSk",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+421",
        },
        geo: {
            centerLat: 48.669026,
            centerLng: 19.699024,
            swLat: 47.731159,
            swLng: 16.833182,
            neLat: 49.613805,
            neLng: 22.558934,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.FR]: {
        country: "FR",
        countryName: "France",
        localizedCountryName: "France",
        language: ["fr"],
        cmsLocaleTagId: "localeFrFr",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+33",
        },
        geo: {
            centerLat: 46.227638,
            centerLng: 2.213749,
            swLat: 41.342328,
            swLng: -5.141228,
            neLat: 51.089166,
            neLng: 9.560068,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
    },
    [Market.AT]: {
        country: "AT",
        countryName: "Austria",
        localizedCountryName: "Österreich",
        language: ["de"],
        cmsLocaleTagId: "localeDeAt",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+43",
            parcelShops: {
                searchMethod: "address",
            },
        },
        geo: {
            centerLat: 47.516231,
            centerLng: 14.550072,
            swLat: 46.372336,
            swLng: 9.530783,
            neLat: 49.020608,
            neLng: 17.160686,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.CH]: {
        country: "CH",
        countryName: "Switzerland",
        localizedCountryName: "Schweiz",
        language: ["de", "fr"],
        cmsLocaleTagId: "localeDeCh",
        currency: "CHF",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+41",
        },
        geo: {
            centerLat: 46.818188,
            centerLng: 8.227512,
            swLat: 45.81792,
            swLng: 5.95608,
            neLat: 47.808455,
            neLng: 10.49234,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.DE]: {
        country: "DE",
        countryName: "Germany",
        localizedCountryName: "Deutschland",
        language: ["de"],
        cmsLocaleTagId: "localeDeDe",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+49",
            hasDHLExtraFields: true,
        },
        geo: {
            centerLat: 51.165691,
            centerLng: 10.451526,
            swLat: 47.270112,
            swLng: 5.866342,
            neLat: 55.058347,
            neLng: 15.041896,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasRaffle: isRaffleEnabled,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.NL]: {
        country: "NL",
        countryName: "Netherlands",
        localizedCountryName: "Nederland",
        language: ["nl"],
        cmsLocaleTagId: "localeNlNl",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+31",
        },
        geo: {
            centerLat: 52.132633,
            centerLng: 5.291266,
            swLat: 50.750384,
            swLng: 3.357962,
            neLat: 53.556021,
            neLng: 7.22751,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.DK]: {
        country: "DK",
        countryName: "Denmark",
        localizedCountryName: "Danmark",
        language: ["da"],
        cmsLocaleTagId: "localeDaDk",
        currency: "DKK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+45",
        },
        geo: {
            centerLat: 56.1554671,
            centerLng: 10.4309955,
            swLat: 54.464597,
            swLng: 8.072241,
            neLat: 57.751813,
            neLng: 15.197281,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasRaffle: isRaffleEnabled,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.FI]: {
        country: "FI",
        countryName: "Finland",
        localizedCountryName: "Suomi",
        language: ["fi"],
        cmsLocaleTagId: "localeFiFi",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+358",
        },
        geo: {
            centerLat: 61.92411,
            centerLng: 25.748151,
            swLat: 59.70544,
            swLng: 20.547411,
            neLat: 70.092293,
            neLng: 31.5871,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: isRaffleEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.IE]: {
        country: "IE",
        countryName: "Ireland",
        localizedCountryName: "Ireland",
        language: ["en"],
        cmsLocaleTagId: "localeEnIe",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.UK, SizeLocales.EU, SizeLocales.CM],
        checkout: {
            countryCode: "+353",
        },
        geo: {
            centerLat: 53.41291,
            centerLng: -8.24389,
            swLat: 51.421938,
            swLng: -10.66958,
            neLat: 55.38849,
            neLng: -5.9947,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.LT]: {
        country: "LT",
        countryName: "Lithuania",
        localizedCountryName: "Lietuva",
        language: ["lt"],
        cmsLocaleTagId: "localeLtLt",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+370",
        },
        geo: {
            centerLat: 55.169438,
            centerLng: 23.881275,
            swLat: 53.896879,
            swLng: 20.954368,
            neLat: 56.450321,
            neLng: 26.835591,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.LV]: {
        country: "LV",
        countryName: "Latvia",
        localizedCountryName: "Latvija",
        language: ["lv"],
        cmsLocaleTagId: "localeLvLv",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+371",
        },
        geo: {
            centerLat: 56.879635,
            centerLng: 24.603189,
            swLat: 55.674777,
            swLng: 20.962343,
            neLat: 58.085569,
            neLng: 28.241403,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
    [Market.NO]: {
        country: "NO",
        countryName: "Norway",
        localizedCountryName: "Norge",
        language: ["nb"],
        cmsLocaleTagId: "localeNbNo",
        currency: "NOK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+47",
        },
        geo: {
            centerLat: 60.472024,
            centerLng: 8.468946,
            swLat: 57.959744,
            swLng: 4.500096,
            neLat: 71.185476,
            neLng: 31.168268,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: isRaffleEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.SE]: {
        country: "SE",
        countryName: "Sweden",
        localizedCountryName: "Sverige",
        language: ["sv"],
        cmsLocaleTagId: "localeSvSe",
        currency: "SEK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+46",
        },
        geo: {
            centerLat: 60.128161,
            centerLng: 18.643501,
            swLat: 55.32872,
            swLng: 10.963187,
            neLat: 69.059971,
            neLng: 24.166809,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: isRaffleEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
    },
    [Market.CY]: {
        country: "CY",
        countryName: "Cyprus",
        localizedCountryName: "Cyprus",
        language: ["en"],
        cmsLocaleTagId: "localeEnCy",
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+357",
        },
        geo: {
            centerLat: 35.1264,
            centerLng: 33.4299,
            swLat: 34.5,
            swLng: 32.2,
            neLat: 35.7,
            neLng: 34.6,
            initZoomLevel: 9,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.CY],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        extraPriceType: [ExtraPriceType.OMNIBUS],
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        trustpilotIntegration: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
        hasLocalizedCategoryFacets: isLocalizedCategoryFacet,
        liveChat: isLiveChatEnabled,
        hasRaffle: isRaffleEnabled,
    },
};
